import React, { useState } from 'react';
import './index.css';
import { supabase } from './lib/supabase';

import ToolLauncher from './components/ToolLauncher';
import MetaGenerator from './components/MetaGenerator';
import ContentBriefTool from './components/ContentBriefTool';
import FAQCreator from './components/FAQCreator';
import ReportTool from './components/report/ReportTool';
import IntakeForm from './components/intake/IntakeForm';
import VolumeChecker from './components/VolumeChecker';
import CompetitiveAnalysis from './components/CompetitiveAnalysis';
import Dashboard from './components/dashboard/Dashboard';
import ClientDetail from './components/dashboard/ClientDetail';
import StepClientSetup from './components/StepClientSetup';
import StepServices from './components/StepServices';
import StepReviewKeywords from './components/StepReviewKeywords';
import StepLocations from './components/StepLocations';
import StepMatrix from './components/StepMatrix';
import StepFetching from './components/StepFetching';
import StepReport from './components/StepReport';

import { suggestKeywords, fetchVolumes, fetchRankings } from './utils/api';
import ContentBriefs from './components/dashboard/ContentBriefs';

// ── View names ───────────────────────────────────────────────
// 'dashboard'     — client list
// 'client'        — client detail / campaign history
// 'research'      — 7-step keyword research workflow
// 'campaign'      — view a saved campaign report

const STEPS = [
  { id: 1, label: 'Client setup' },
  { id: 2, label: 'Services' },
  { id: 3, label: 'Review keywords' },
  { id: 4, label: 'Locations' },
  { id: 5, label: 'Keyword matrix' },
  { id: 6, label: 'Fetching data' },
  { id: 7, label: 'Report' },
];

const FETCH_STEPS = [
  { label: 'Connecting to DataForSEO API…' },
  { label: 'Sending keyword batch for volume data…' },
  { label: 'Retrieving monthly search volumes…' },
  { label: 'Fetching client ranking positions…' },
  { label: 'Building keyword groups…' },
  { label: 'Done!' },
];

const CORRECT_PASSWORD = process.env.REACT_APP_TOOL_PASSWORD || 'imsr2024';

function PasswordGate({ onUnlock }) {
  const [input, setInput] = React.useState('');
  const [error, setError] = React.useState(false);
  const [shake, setShake] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === CORRECT_PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setError(false), 2000);
      setInput('');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#1A2744', fontFamily: "-apple-system, 'Helvetica Neue', Arial, sans-serif"
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '2.5rem 2rem', width: '100%', maxWidth: 360,
        textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animation: shake ? 'shake 0.4s ease' : 'none'
      }}>
        <img src="/imsr-logo.png" alt="IMSR" style={{ width: 180, marginBottom: '1.5rem', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1A2744', marginBottom: 6 }}>SEO Toolkit</div>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: '1.5rem' }}>Enter your password to continue</div>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="Password"
            value={input}
            onChange={e => setInput(e.target.value)}
            autoFocus
            style={{
              width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8,
              border: `1.5px solid ${error ? '#EF4444' : '#D1D5DB'}`,
              outline: 'none', marginBottom: 12, boxSizing: 'border-box',
              transition: 'border-color 0.2s', textAlign: 'center', letterSpacing: '0.15em'
            }}
          />
          {error && <div style={{ fontSize: 12, color: '#EF4444', marginBottom: 10 }}>Incorrect password — please try again</div>}
          <button type="submit" style={{
            width: '100%', padding: '10px', fontSize: 14, fontWeight: 600,
            background: '#1A2744', color: '#fff', border: 'none', borderRadius: 8,
            cursor: 'pointer'
          }}>
            Enter toolkit →
          </button>
        </form>
      </div>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}

export default function App() {
  // ── Navigation state ──────────────────────────────────────
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('imsr_auth') === 'true');
  const [view, setView] = useState('launcher');
  const [activeTool, setActiveTool] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [briefKeywords, setBriefKeywords] = useState([]);

  // ── Research workflow state ───────────────────────────────
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [client, setClient] = useState({ businessName: '', url: '', description: '', country: 'GB', locationCode: 2826 });
  const [services, setServices] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [locationData, setLocationData] = useState({ postcode: '', radius: 10, towns: [], selectedTowns: [] });
  const [sectors, setSectors] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [fetchStepStatus, setFetchStepStatus] = useState(FETCH_STEPS.map(s => ({ ...s, status: 'pending' })));
  const [keywordGroups, setKeywordGroups] = useState([]);

  const selectTool = (toolId) => {
    setActiveTool(toolId);
    if (toolId === 'keyword-research') setView('dashboard');
    else if (toolId === 'meta-generator') setView('meta-generator');
    else if (toolId === 'content-brief') setView('content-brief');
    else if (toolId === 'faq-creator') setView('faq-creator');
    else if (toolId === 'report') setView('report');
    else if (toolId === 'intake') setView('intake');
    else if (toolId === 'volume-checker') setView('volume-checker');
    else if (toolId === 'competitive') setView('competitive');
    else setView('launcher');
  };

  const sleep = ms => new Promise(r => setTimeout(r, ms));
  const go = n => { setError(''); setStep(n); };

  const setFetchStatus = (index, status) => {
    setFetchStepStatus(prev => prev.map((s, i) => i === index ? { ...s, status } : s));
  };

  // ── Start new research for a client ──────────────────────
  const startNewCampaign = (clientData) => {
    setActiveClient(clientData);
    setClient({
      businessName: clientData.name,
      url: clientData.url || '',
      description: clientData.description || '',
      country: clientData.country || 'GB',
      locationCode: clientData.location_code || 2826,
    });
    setServices([]);
    setSuggestions({});
    setLocationData({ postcode: '', radius: 10, towns: [], selectedTowns: [] });
    setSectors([]);
    setMatrix([]);
    setKeywordGroups([]);
    setStep(1);
    setView('research');
  };

  // ── Open a saved campaign ─────────────────────────────────
  const openCampaign = (campaign) => {
    // Reconstruct keyword groups from saved keywords
    const groups = {};
    (campaign.keywords || []).forEach(kw => {
      const key = kw.service || 'General';
      if (!groups[key]) groups[key] = [];
      groups[key].push({
        keyword: kw.keyword,
        location: kw.location,
        sector: kw.sector,
        searchVolume: kw.search_volume || 0,
        rank: kw.rank === '100+' ? 101 : parseInt(kw.rank) || 101,
        priority: kw.priority || 'Unset',
        savedId: kw.id
      });
    });

    const groupArray = Object.entries(groups).map(([name, keywords]) => ({ name, keywords }));
    setKeywordGroups(groupArray);
    setActiveCampaign(campaign);
    setStep(7);
    setView('campaign');
  };

  // ── Step 2→3: Generate keyword suggestions ────────────────
  const handleGenerateKeywords = async () => {
    setLoading(true);
    setError('');
    try {
      const cleanServices = services.filter(s => s.trim());
      const result = await suggestKeywords(cleanServices, client.description);
      const wrapped = {};
      Object.entries(result.suggestions).forEach(([svc, kws]) => {
        wrapped[svc] = kws.map(kw => ({ kw, state: 'selected' }));
      });
      setSuggestions(wrapped);
      go(3);
    } catch (e) {
      setError('Failed to generate keyword suggestions. Check your API connection.');
    }
    setLoading(false);
  };

  // ── Step 4→5: Build keyword matrix ───────────────────────
  const handleBuildMatrix = () => {
    const towns = locationData.selectedTowns || locationData.towns || [];
    const built = [];
    Object.entries(suggestions).forEach(([service, kws]) => {
      const approved = kws.filter(k => k.state === 'selected').map(k => k.kw);
      approved.forEach(kw => {
        towns.forEach(town => {
          built.push({ keyword: `${kw} ${town.toLowerCase()}`, service, location: town });
        });
        sectors.forEach(sector => {
          towns.forEach(town => {
            built.push({ keyword: `${sector.toLowerCase()} ${kw} ${town.toLowerCase()}`, service, location: town, sector });
          });
        });
      });
    });
    setMatrix(built);
    go(5);
  };

  // ── Step 5→7: Fetch data + save to Supabase ───────────────
  const handleFetchData = async () => {
    go(6);
    setFetchStepStatus(FETCH_STEPS.map(s => ({ ...s, status: 'pending' })));

    try {
      const keywords = matrix.map(k => k.keyword);

      setFetchStatus(0, 'active'); await sleep(400); setFetchStatus(0, 'done');
      setFetchStatus(1, 'active'); await sleep(300); setFetchStatus(1, 'done');

      // Volumes
      setFetchStatus(2, 'active');
      const volumeData = await fetchVolumes(keywords, client.locationCode);
      setFetchStatus(2, 'done');

      // Rankings — only for keywords with volume
      setFetchStatus(3, 'active');
      const domain = client.url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
      const keywordsWithVolume = keywords.filter(kw => {
        const normKw = kw.toLowerCase().trim().replace(/\s+/g, ' ');
        const volInfo = volumeData.volumes?.[normKw] || volumeData.volumes?.[kw] || {};
        return (volInfo.searchVolume || 0) > 0;
      });
      const rankingData = await fetchRankings(keywordsWithVolume, domain, client.locationCode);
      setFetchStatus(3, 'done');

      // Build groups
      setFetchStatus(4, 'active');
      const groups = {};
      matrix.forEach(item => {
        const normKw = item.keyword.toLowerCase().trim().replace(/\s+/g, ' ');
        const volInfo = volumeData.volumes?.[normKw] || volumeData.volumes?.[item.keyword] || {};
        const rankInfo = rankingData.rankings?.[normKw] || rankingData.rankings?.[item.keyword] || {};
        if (!groups[item.service]) groups[item.service] = [];
        groups[item.service].push({
          keyword: item.keyword,
          location: item.location,
          sector: item.sector || null,
          searchVolume: volInfo.searchVolume || 0,
          rank: rankInfo.rank === '100+' ? 101 : (rankInfo.rank || 101),
          competition: volInfo.competition || null,
          cpc: volInfo.cpc || null,
        });
      });

      const groupArray = Object.entries(groups).map(([name, keywords]) => ({
        name,
        keywords: keywords.sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0)),
      }));

      // Save campaign to Supabase
      if (activeClient) {
        try {
          // Save campaign record
          const { data: campaign, error: campErr } = await supabase
            .from('keyword_campaigns')
            .insert([{
              client_id: activeClient.id,
              postcode: locationData.postcode,
              radius: locationData.radius,
              services: services,
              towns: locationData.selectedTowns || locationData.towns,
              sectors: sectors,
            }])
            .select()
            .single();

          if (!campErr && campaign) {
            // Save all keywords
            const kwRows = groupArray.flatMap(group =>
              group.keywords.map(kw => ({
                campaign_id: campaign.id,
                keyword: kw.keyword,
                service: group.name,
                location: kw.location,
                sector: kw.sector || null,
                search_volume: kw.searchVolume || 0,
                rank: kw.rank > 100 ? '100+' : String(kw.rank),
                priority: 'Unset',
              }))
            );
            await supabase.from('keywords').insert(kwRows);
            setActiveCampaign(campaign);
          }
        } catch (saveErr) {
          console.error('Could not save campaign to Supabase:', saveErr);
        }
      }

      setFetchStatus(4, 'done');
      setFetchStatus(5, 'done');
      await sleep(600);
      setKeywordGroups(groupArray);
      go(7);

    } catch (e) {
      console.error(e);
      setError('DataForSEO fetch failed. Check your credentials and try again.');
      go(5);
    }
  };

  // ── Save priority updates back to Supabase ────────────────
  const handlePriorityUpdate = async (keyword, priority) => {
    if (!activeCampaign) return;
    await supabase
      .from('keywords')
      .update({ priority })
      .eq('campaign_id', activeCampaign.id)
      .eq('keyword', keyword);
  };

  // ── Sidebar step labels ───────────────────────────────────
  const sidebarLabel = () => {
    if (view === 'dashboard') return 'All clients';
    if (view === 'client') return activeClient?.name || 'Client';
    if (view === 'campaign') return 'Campaign report';
    return 'Keyword research';
  };

  const isResearching = view === 'research' || view === 'campaign';

  if (!unlocked) {
    return <PasswordGate onUnlock={() => { sessionStorage.setItem('imsr_auth', 'true'); setUnlocked(true); }} />;
  }

  return (
    <div className="app-shell">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-logo" style={{ padding: '1rem 1.25rem' }}>
          <img
            src="/imsr-logo.png"
            alt="Improve My Search Ranking"
            style={{ width: '100%', maxWidth: 180, display: 'block', opacity: 0.92 }}
          />
          <div className="sidebar-logo-sub" style={{ marginTop: 6 }}>SEO Toolkit</div>
        </div>

        <div className="sidebar-steps">
          {/* Navigation */}
          <div
            className={`sidebar-step ${view === 'launcher' ? 'active' : ''}`}
            onClick={() => setView('launcher')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num ${view === 'launcher' ? 'active' : ''}`}>⊞</div>
            <div className={`step-label ${view === 'launcher' ? 'active' : ''}`}>All tools</div>
          </div>

          <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '6px 1.5rem' }} />

          <div
            className={`sidebar-step ${view === 'dashboard' || view === 'client' || view === 'research' || view === 'campaign' || view === 'briefs' ? 'active' : ''}`}
            onClick={() => selectTool('keyword-research')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>🔍</div>
            <div className={`step-label ${view === 'dashboard' || view === 'client' ? 'active' : ''}`}>Keyword Research</div>
          </div>

          <div
            className={`sidebar-step ${view === 'meta-generator' ? 'active' : ''}`}
            onClick={() => selectTool('meta-generator')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>🏷️</div>
            <div className={`step-label ${view === 'meta-generator' ? 'active' : ''}`}>Meta Generator</div>
          </div>

          <div
            className={`sidebar-step ${view === 'content-brief' ? 'active' : ''}`}
            onClick={() => selectTool('content-brief')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>✍️</div>
            <div className={`step-label ${view === 'content-brief' ? 'active' : ''}`}>Content Brief</div>
          </div>

          <div
            className={`sidebar-step ${view === 'faq-creator' ? 'active' : ''}`}
            onClick={() => selectTool('faq-creator')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>❓</div>
            <div className={`step-label ${view === 'faq-creator' ? 'active' : ''}`}>FAQ Creator</div>
          </div>

          <div
            className={`sidebar-step ${view === 'report' ? 'active' : ''}`}
            onClick={() => selectTool('report')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>📊</div>
            <div className={`step-label ${view === 'report' ? 'active' : ''}`}>Monthly Report</div>
          </div>

          <div
            className={`sidebar-step ${view === 'competitive' ? 'active' : ''}`}
            onClick={() => selectTool('competitive')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>🔬</div>
            <div className={`step-label ${view === 'competitive' ? 'active' : ''}`}>Competitive</div>
          </div>

          <div
            className={`sidebar-step ${view === 'volume-checker' ? 'active' : ''}`}
            onClick={() => selectTool('volume-checker')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>📈</div>
            <div className={`step-label ${view === 'volume-checker' ? 'active' : ''}`}>Volume Checker</div>
          </div>

          <div
            className={`sidebar-step ${view === 'intake' ? 'active' : ''}`}
            onClick={() => selectTool('intake')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`step-num`}>📋</div>
            <div className={`step-label ${view === 'intake' ? 'active' : ''}`}>SEO Kick-Off</div>
          </div>

          {activeClient && (
            <div
              className={`sidebar-step ${view === 'client' ? 'active' : ''}`}
              onClick={() => setView('client')}
              style={{ cursor: 'pointer' }}
            >
              <div className={`step-num ${view === 'client' ? 'active' : ''}`}>
                {activeClient.name.charAt(0).toUpperCase()}
              </div>
              <div className={`step-label ${view === 'client' ? 'active' : ''}`}>{activeClient.name}</div>
            </div>
          )}

          {view === 'briefs' && (
            <div className="sidebar-step active">
              <div className="step-num active">✍</div>
              <div className="step-label active">Content briefs</div>
            </div>
          )}

          {/* Research steps */}
          {isResearching && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '8px 1.5rem' }} />
              {STEPS.map(s => {
                const isDone = step > s.id;
                const isActive = step === s.id && view === 'research';
                return (
                  <div
                    key={s.id}
                    className={`sidebar-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
                    onClick={() => isDone && view === 'research' && go(s.id)}
                    style={{ cursor: isDone && view === 'research' ? 'pointer' : 'default' }}
                  >
                    <div className={`step-num ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                      {isDone ? '✓' : s.id}
                    </div>
                    <div className={`step-label ${isActive ? 'active' : ''}`}>{s.label}</div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="sidebar-footer">improvemysearchranking.com</div>
      </div>

      {/* Main content */}
      <div className="main">
        <div className="main-inner">
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              {error}
              <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 600 }}>×</button>
            </div>
          )}

          {/* Tool launcher */}
          {view === 'launcher' && (
            <ToolLauncher onSelectTool={selectTool} />
          )}

          {/* Competitive analysis */}
          {view === 'competitive' && (
            <CompetitiveAnalysis onBack={() => setView('launcher')} />
          )}

          {/* Volume checker */}
          {view === 'volume-checker' && (
            <VolumeChecker onBack={() => setView('launcher')} />
          )}

          {/* Intake form */}
          {view === 'intake' && (
            <IntakeForm onBack={() => setView('launcher')} />
          )}

          {/* Report tool */}
          {view === 'report' && (
            <ReportTool onBack={() => setView('launcher')} />
          )}

          {/* FAQ creator */}
          {view === 'faq-creator' && (
            <FAQCreator onBack={() => setView('launcher')} />
          )}

          {/* Content brief tool */}
          {view === 'content-brief' && (
            <ContentBriefTool onBack={() => setView('launcher')} />
          )}

          {/* Meta generator */}
          {view === 'meta-generator' && (
            <MetaGenerator onBack={() => setView('launcher')} />
          )}

          {/* Dashboard */}
          {view === 'dashboard' && (
            <Dashboard
              onSelectClient={c => { setActiveClient(c); setView('client'); }}
              onNewClient={() => {}}
            />
          )}

          {/* Client detail */}
          {view === 'client' && activeClient && (
            <ClientDetail
              client={activeClient}
              onBack={() => setView('dashboard')}
              onNewCampaign={startNewCampaign}
              onOpenCampaign={c => { setActiveCampaign(c); openCampaign(c); }}
              onEditClient={c => setActiveClient(c)}
              onCreateBriefs={(kws, fullClient) => { setBriefKeywords(kws); if (fullClient) setActiveClient(fullClient); setView('briefs'); }}
            />
          )}

          {/* Content briefs */}
          {view === 'briefs' && (
            <ContentBriefs
              keywords={briefKeywords}
              client={{ ...activeClient, description: activeClient?.description }}
              onBack={() => setView('client')}
            />
          )}

          {/* Research workflow */}
          {view === 'research' && (
            <>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${(step / 7) * 100}%` }} />
              </div>

              {step === 1 && <StepClientSetup data={client} onChange={setClient} onNext={() => go(2)} />}
              {step === 2 && <StepServices services={services} onChange={setServices} loading={loading} onBack={() => go(1)} onNext={handleGenerateKeywords} />}
              {step === 3 && <StepReviewKeywords suggestions={suggestions} onChange={setSuggestions} onBack={() => go(2)} onNext={() => go(4)} />}
              {step === 4 && <StepLocations data={locationData} onChange={setLocationData} sectors={sectors} onSectorsChange={setSectors} onBack={() => go(3)} onNext={handleBuildMatrix} />}
              {step === 5 && <StepMatrix matrix={matrix} loading={loading} onBack={() => go(4)} onNext={handleFetchData} />}
              {step === 6 && <StepFetching steps={fetchStepStatus} />}
              {step === 7 && (
                <StepReport
                  client={client}
                  keywordGroups={keywordGroups}
                  onBack={() => go(5)}
                  onPriorityUpdate={handlePriorityUpdate}
                  onDone={() => { setView('client'); }}
                />
              )}
            </>
          )}

          {/* Saved campaign report */}
          {view === 'campaign' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <button className="btn btn-sm" onClick={() => setView('client')}>← Back to campaigns</button>
              </div>
              <StepReport
                client={{ businessName: activeClient?.name, url: activeClient?.url }}
                keywordGroups={keywordGroups}
                onBack={() => setView('client')}
                onPriorityUpdate={handlePriorityUpdate}
                onDone={() => setView('client')}
                savedCampaign
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
